import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { apiRequest } from './apiService';

// @architect-approved: HARDCODED_COLORS - Navigation chrome fallback defaults
// (hover shadow, branding-blue-derived hover tint, dark overlay scrim, cream
// text color) used when no DB-driven navigation theming is configured;
// decorative chrome fallback, not brand customization surface.

/**
 * Navigation Chrome Configuration Interface
 * Typed configuration for database-driven navigation theming with five-tier fallback
 *
 * Hierarchy:
 * TTP_ID (data partition) → Domain Code → Org UUID → Navigation Pattern → Component Seq
 *
 * Five-Tier Fallback:
 * 1. Component-specific (TTP_ID + domain + org + pattern + seq) - Priority 1
 * 2. Pattern-level (TTP_ID + domain + org + pattern) - Priority 2
 * 3. Org-default (TTP_ID + org) - Priority 3
 * 4. Domain-default (TTP_ID + domain + system UUID) - Priority 4
 * 5. System-default (TTP_ID + TGC + system UUID) - Priority 5
 */

interface NavigationChromeConfig {
    // Directory Host (dirm)
    dirm: {
        container: {
            outer: {
                backgroundColor: string;
                minHeight: string;
                padding: string;
            };
            inner: {
                backgroundColor: string;
                borderRadius: string;
                padding: string;
            };
        };
        card: {
            backgroundColor: string;
            borderColor: string;
            hoverShadow: string;
            iconColor: string;
            buttonBorderColor: string;
            buttonHoverBackground: string;
        };
    };

    // Elevator Shaft
    shaft: {
        container: {
            backgroundColor: string;
            minHeight: string;
            padding: string;
        };
    };

    // Access Badge Gate
    gate: {
        overlay: {
            backgroundColor: string;
            textColor: string;
        };
        container: {
            minHeight: string;
        };
    };

    // Floating Directory
    floating: {
        drawer: {
            backgroundColor: string;
            width: string;
            padding: string;
        };
    };

    // Metadata
    metadata: {
        bindingId: number | null;
        priorityLabel: string;
        themeCollectionId: number | null;
        themeName: string;
    };
}

/**
 * Default Navigation Chrome Configuration (MUI Theme Tokens)
 * Used as fallback when API fails or no binding exists
 */
const DEFAULT_CHROME_CONFIG: NavigationChromeConfig = {
    dirm: {
        container: {
            outer: {
                backgroundColor: 'background.default',
                minHeight: '100vh',
                padding: '48px',
            },
            inner: {
                backgroundColor: 'background.paper',
                borderRadius: '26px 26px 0 0',
                padding: '48px',
            },
        },
        card: {
            backgroundColor: 'background.paper',
            borderColor: 'divider',
            hoverShadow: '0 4px 20px rgba(0,0,0,0.1)',
            iconColor: 'primary.main',
            buttonBorderColor: 'primary.main',
            buttonHoverBackground: 'rgba(25, 118, 210, 0.04)',
        },
    },
    shaft: {
        container: {
            backgroundColor: 'background.default',
            minHeight: '100vh',
            padding: '24px',
        },
    },
    gate: {
        overlay: {
            backgroundColor: 'rgba(27, 26, 23, 0.75)',
            textColor: '#f4f0e6',
        },
        container: {
            minHeight: '100vh',
        },
    },
    floating: {
        drawer: {
            backgroundColor: 'background.paper',
            width: '320px',
            padding: '16px',
        },
    },
    metadata: {
        bindingId: null,
        priorityLabel: 'Default',
        themeCollectionId: null,
        themeName: 'System Default',
    },
};

/**
 * Parse Theme Variables from API Response
 * Converts flat variable array into typed NavigationChromeConfig object
 */
function parseThemeVariables(themeVariables: any[]): Partial<NavigationChromeConfig> {
    const config: any = {
        dirm: {
            container: { outer: {}, inner: {} },
            card: {},
        },
        shaft: {
            container: {},
        },
        gate: {
            overlay: {},
            container: {},
        },
        floating: {
            drawer: {},
        },
    };

    // Group variables by code_section and element
    themeVariables.forEach((section) => {
        const codeSection = section.code_section;

        section.elements?.forEach((element: any) => {
            element.variables?.forEach((variable: any) => {
                const varName = variable.variable;
                const defaultValue = variable.default_value;

                // Map code_section → config path
                if (codeSection === 'nav_dirm_container') {
                    if (element.element === 'outer') {
                        config.dirm.container.outer[varName] = defaultValue;
                    } else if (element.element === 'inner') {
                        config.dirm.container.inner[varName] = defaultValue;
                    }
                } else if (codeSection === 'nav_dirm_card') {
                    config.dirm.card[varName] = defaultValue;
                } else if (codeSection === 'nav_shaft_container') {
                    config.shaft.container[varName] = defaultValue;
                } else if (codeSection === 'nav_gate_container') {
                    if (element.element === 'overlay') {
                        config.gate.overlay[varName] = defaultValue;
                    } else if (element.element === 'container') {
                        config.gate.container[varName] = defaultValue;
                    }
                } else if (codeSection === 'nav_floating_container') {
                    config.floating.drawer[varName] = defaultValue;
                }
            });
        });
    });

    return config;
}

/**
 * useNavigationChrome Hook
 *
 * Fetches database-driven navigation chrome configuration with five-tier fallback
 *
 * @param pattern - Navigation pattern: 'dirm' | 'shaft' | 'gate' | 'floating'
 * @param componentSeq - Optional component sequence for component-specific override
 * @returns NavigationChromeConfig object with container/card/drawer styling
 *
 * @example
 * ```tsx
 * // Directory host
 * const chrome = useNavigationChrome('dirm', 10000);
 * <Box sx={{ background: chrome.dirm.container.outer.backgroundColor }}>
 *
 * // Elevator shaft
 * const chrome = useNavigationChrome('shaft');
 * <Box sx={{ background: chrome.shaft.container.backgroundColor }}>
 * ```
 */
export function useNavigationChrome(
    pattern: 'dirm' | 'shaft' | 'gate' | 'floating',
    componentSeq?: number,
): NavigationChromeConfig {
    const { activeOrganizationUUID } = useAuth();
    const [chromeConfig, setChromeConfig] = useState<NavigationChromeConfig>(DEFAULT_CHROME_CONFIG);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        async function fetchChromeConfig() {
            try {
                setLoading(true);

                // Guard: No active org, use default
                if (!activeOrganizationUUID) {
                    console.warn(
                        '[useNavigationChrome] No active organization, using default chrome config',
                    );
                    setChromeConfig(DEFAULT_CHROME_CONFIG);
                    return;
                }

                // Fetch from API with five-tier fallback (@Manage=8)
                // Convert 'ALL' → platform default GUID (00000000-0000-0000-0000-000000000000)
                // When user filters "All Organizations", use platform default branding
                // When user selects specific org, branding flips to that tenant's white-label theme
                const PLATFORM_DEFAULT_ORG = '00000000-0000-0000-0000-000000000000';
                const orgUuid =
                    activeOrganizationUUID && activeOrganizationUUID !== 'ALL'
                        ? activeOrganizationUUID
                        : PLATFORM_DEFAULT_ORG;

                const response = await apiRequest('/api/navigation-chrome', 'POST', {
                    manage: 8, // PLATFORM EXPORT (five-tier fallback)
                    ttp_id: 0, // Production (TODO: Get from auth context when multi-TTP support added)
                    Domain_Code: 'TGC', // TODO: Get from auth context or component context
                    Org_UUID: orgUuid,
                    Navigation_Pattern: pattern,
                    Component_Seq: componentSeq,
                });

                const data = response.data?.[0];

                if (!data) {
                    console.warn(
                        '[useNavigationChrome] No binding found, using default chrome config',
                    );
                    setChromeConfig(DEFAULT_CHROME_CONFIG);
                    return;
                }

                // Parse theme variables
                const themeVariables = data.theme_variables || [];
                const parsedConfig = parseThemeVariables(themeVariables);

                // Merge with defaults (defensive fallback for missing variables)
                const mergedConfig: NavigationChromeConfig = {
                    dirm: {
                        container: {
                            outer: {
                                ...DEFAULT_CHROME_CONFIG.dirm.container.outer,
                                ...parsedConfig.dirm?.container?.outer,
                            },
                            inner: {
                                ...DEFAULT_CHROME_CONFIG.dirm.container.inner,
                                ...parsedConfig.dirm?.container?.inner,
                            },
                        },
                        card: {
                            ...DEFAULT_CHROME_CONFIG.dirm.card,
                            ...parsedConfig.dirm?.card,
                        },
                    },
                    shaft: {
                        container: {
                            ...DEFAULT_CHROME_CONFIG.shaft.container,
                            ...parsedConfig.shaft?.container,
                        },
                    },
                    gate: {
                        overlay: {
                            ...DEFAULT_CHROME_CONFIG.gate.overlay,
                            ...parsedConfig.gate?.overlay,
                        },
                        container: {
                            ...DEFAULT_CHROME_CONFIG.gate.container,
                            ...parsedConfig.gate?.container,
                        },
                    },
                    floating: {
                        drawer: {
                            ...DEFAULT_CHROME_CONFIG.floating.drawer,
                            ...parsedConfig.floating?.drawer,
                        },
                    },
                    metadata: {
                        bindingId: data.nav_binding_id,
                        priorityLabel: data.priority_label || 'Unknown',
                        themeCollectionId: data.theme_collection_id,
                        themeName: data.theme_name || 'Unknown',
                    },
                };

                setChromeConfig(mergedConfig);

                console.log('[useNavigationChrome] Chrome config loaded', {
                    pattern,
                    componentSeq,
                    priorityLabel: mergedConfig.metadata.priorityLabel,
                    themeName: mergedConfig.metadata.themeName,
                });
            } catch (error) {
                console.error('[useNavigationChrome] Failed to fetch chrome config:', error);
                setChromeConfig(DEFAULT_CHROME_CONFIG);
            } finally {
                setLoading(false);
            }
        }

        fetchChromeConfig();
    }, [activeOrganizationUUID, pattern, componentSeq]);

    return chromeConfig;
}
